// WAAP Advanced Protection → Page Defacement Mitigation.
//
// Per-website feature stored inside the site-config object under `config.tamper`.
// Backed by the PRIVATE portal /site(s) via eagle_waf/website (read-modify-write,
// like the Configuration tab). Account-level: the `:id` domain only resolves the
// customer session; a site is addressed by its portal site-config id (carried from
// the list into the edit form). So the hooks take an ANCHOR domain UUID:
//
//   GET /waap/domains/:id/page-defacement            → portal GET /sites
//   GET /waap/domains/:id/page-defacement/:siteId    → portal GET /site/:siteId
//   PUT /waap/domains/:id/page-defacement/:siteId    → portal PUT /site (patch config.tamper)

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

// ---- Types ----------------------------------------------------------------

/** One page URL path cached for defacement protection. */
export interface TamperCacheURL {
  /** Relative path, leading slash required (e.g. "/index.html"). The vendor
   *  rejects absolute URLs and paths without a leading slash with a 500. */
  path: string;
  /** snake_case to match the backend wire contract (GET emits / PUT reads
   *  `is_regex`); the vendor's own camelCase `isRegex` is translated BE-side. */
  is_regex: boolean;
  /** Content types to cache (e.g. ["text/html"]). */
  type: string[];
}

/** Normalize a user-entered URL/path to the vendor-accepted form: a relative
 *  path with a leading slash. A full URL keeps only its path (+ query); a
 *  slashless path gets a leading slash. Verified: absolute or slashless paths
 *  make the vendor /site PUT 500. */
export function toRelativePath(input: string): string {
  const v = input.trim();
  if (!v) return '';
  const m = v.match(/^https?:\/\/[^/]+(\/.*)?$/i);
  if (m) return m[1] || '/';
  return v.startsWith('/') ? v : `/${v}`;
}

/** A Page Defacement rule (the site's `config.tamper` subset). */
export interface PageDefacementConfig {
  enabled: boolean;
  /** Maximum cache space in MB. */
  disk_space: number;
  /** Automatically update cache on expiry. */
  auto_update: boolean;
  /** Cache expiry duration in minutes. */
  expire_time: number;
  /** Similarity threshold percentage. */
  web_similarity: number;
  cache_urls: TamperCacheURL[];
}

/** A row in the Page Defacement website list. */
export interface PageDefacementSite {
  site_id: number;
  domain: string;
  enabled: boolean;
}

/** Content-type options for a cached URL path (multi-select; matches the vendor
 *  portal, all seven verified to round-trip). */
export const TAMPER_CONTENT_TYPES = [
  'text/html',
  'text/plain',
  'application/rtf',
  'image/gif',
  'image/jpeg',
  'image/png',
  'application/octet-stream',
] as const;

/** Vendor defaults for a freshly enabled rule (mirrors the portal form). */
export function defaultPageDefacement(): PageDefacementConfig {
  return {
    enabled: false,
    disk_space: 60,
    auto_update: false,
    expire_time: 60,
    web_similarity: 50,
    cache_urls: [],
  };
}

// ---- Paths + query keys --------------------------------------------------

function basePath(domainId: string): string {
  return `/waap/domains/${domainId}/page-defacement`;
}

export const pageDefacementKeys = {
  all: ['waap', 'page-defacement'] as const,
  sites: (domainId: string) => ['waap', 'page-defacement', domainId, 'sites'] as const,
  site: (domainId: string, siteId: string | number) =>
    ['waap', 'page-defacement', domainId, 'site', String(siteId)] as const,
};

// ---- Hooks ---------------------------------------------------------------

/** List every website with its Page Defacement enabled flag. */
export function usePageDefacementSites(domainId: string | undefined) {
  return useQuery({
    queryKey: pageDefacementKeys.sites(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<PageDefacementSite[]> => {
      const res = await serviceApiV3.get<PageDefacementSite[] | { data: PageDefacementSite[] }>(
        basePath(domainId as string),
      );
      const body = res.data as PageDefacementSite[] | { data?: PageDefacementSite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one site's Page Defacement config (for the edit form). */
export function usePageDefacement(domainId: string | undefined, siteId: string | undefined) {
  return useQuery({
    queryKey: pageDefacementKeys.site(domainId ?? '', siteId ?? ''),
    enabled: !!domainId && !!siteId,
    retry: false,
    queryFn: async (): Promise<PageDefacementConfig> => {
      const res = await serviceApiV3.get<PageDefacementConfig | { data: PageDefacementConfig }>(
        `${basePath(domainId as string)}/${siteId}`,
      );
      const body = res.data as PageDefacementConfig | { data?: PageDefacementConfig };
      return (body as { data?: PageDefacementConfig })?.data ?? (body as PageDefacementConfig);
    },
  });
}

/** Update one site's Page Defacement config. */
export function useUpdatePageDefacement(
  domainId: string,
  siteId: number | string,
  options?: UseMutationOptions<unknown, unknown, PageDefacementConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PageDefacementConfig) => {
      const res = await serviceApiV3.put(`${basePath(domainId)}/${siteId}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pageDefacementKeys.sites(domainId) });
      qc.invalidateQueries({ queryKey: pageDefacementKeys.site(domainId, siteId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins -----------------------------------------------
// Used by Web Access Control tab (sites list) which reuses the page-defacement
// site list as the source of all websites on the account.

export const pageDefacementServiceKeys = {
  sites: (serviceId: string) =>
    ['waap', 'page-defacement', 'service', serviceId, 'sites'] as const,
};

export const pageDefacementServiceSiteKeys = {
  site: (serviceId: string, siteId: string | number) =>
    ['waap', 'page-defacement', 'service', serviceId, 'site', String(siteId)] as const,
};

/** Fetch one site's Page Defacement config, service-scoped. */
export function useServicePageDefacement(serviceId: string | undefined, siteId: string | undefined) {
  return useQuery({
    queryKey: pageDefacementServiceSiteKeys.site(serviceId ?? '', siteId ?? ''),
    enabled: !!serviceId && !!siteId,
    retry: false,
    queryFn: async (): Promise<PageDefacementConfig> => {
      const res = await serviceApiV3.get<PageDefacementConfig | { data: PageDefacementConfig }>(
        `/services/${serviceId}/waap/page-defacement/${siteId}`,
      );
      const body = res.data as PageDefacementConfig | { data?: PageDefacementConfig };
      return (body as { data?: PageDefacementConfig })?.data ?? (body as PageDefacementConfig);
    },
  });
}

/** Update one site's Page Defacement config, service-scoped. */
export function useUpdateServicePageDefacement(
  serviceId: string,
  siteId: number | string,
  options?: UseMutationOptions<unknown, unknown, PageDefacementConfig>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PageDefacementConfig) => {
      const res = await serviceApiV3.put(
        `/services/${serviceId}/waap/page-defacement/${siteId}`,
        body,
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: pageDefacementServiceKeys.sites(serviceId) });
      qc.invalidateQueries({
        queryKey: pageDefacementServiceSiteKeys.site(serviceId, siteId),
      });
      options?.onSuccess?.(...args);
    },
  });
}

/** List sites with their Page Defacement flag, resolved from the service. */
export function useServicePageDefacementSites(serviceId: string | undefined) {
  return useQuery({
    queryKey: pageDefacementServiceKeys.sites(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<PageDefacementSite[]> => {
      const res = await serviceApiV3.get<PageDefacementSite[] | { data: PageDefacementSite[] }>(
        `/services/${serviceId}/waap/page-defacement`,
      );
      const body = res.data as PageDefacementSite[] | { data?: PageDefacementSite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}
